import { ColumnDef, Row } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { ArrowUpDown } from 'lucide-react';
import { TClientData } from '@/types/client';
import { ClientActions } from './client-actions';
import { Checkbox } from '@/components/ui/checkbox';

export const defineClientColumns = (
  t: (key: string) => string
): ColumnDef<TClientData>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        className="px-0 min-w-44 md:min-w-36 justify-start"
        aria-label="Client name"
      >
        {t('tableHeaders.clientName')}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),

    cell: ({ row }: { row: Row<TClientData> }) => {
      const name: string = row.getValue('name');

      return (
        <div className="capitalize">
          {name.length > 18 ? `${name.substring(0, 18)}...` : name}
        </div>
      );
    },
  },
  {
    accessorKey: 'address',
    header: ({}) => (
      <div className="px-0 min-w-44 md:min-w-40">
        {t('tableHeaders.address')}
      </div>
    ),

    cell: ({ row }: { row: Row<TClientData> }) => {
      const address: string = row.getValue('address');

      return (
        <div className="capitalize">
          {address.length > 20 ? `${address.substring(0, 20)}...` : address}
        </div>
      );
    },
  },
  {
    accessorKey: 'country',
    header: ({}) => (
      <div className="px-0 min-w-24">{t('tableHeaders.country')}</div>
    ),

    cell: ({ row }: { row: Row<TClientData> }) => (
      <div>{row.getValue('country')}</div>
    ),
  },
  {
    accessorKey: 'city',
    header: ({}) => (
      <div className="px-0 min-w-24">{t('tableHeaders.city')}</div>
    ),

    cell: ({ row }: { row: Row<TClientData> }) => (
      <div>{row.getValue('city')}</div>
    ),
  },
  {
    accessorKey: 'tax',
    header: ({}) => (
      <div className="px-0 min-w-24">{t('tableHeaders.tax')}</div>
    ),

    cell: ({ row }: { row: Row<TClientData> }) => (
      <div className="capitalize">{row.getValue('tax')}</div>
    ),
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }: { row: Row<TClientData> }) => {
      return (
        <div className="flex justify-end">
          <ClientActions client={row.original} t={t} />
        </div>
      );
    },
  },
];
